import React, { useMemo } from 'react'
import { Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import ScrollTable from 'components/global/CUK/ScrollTable'
import TableSkeleton from 'components/global/CUK/Skeletons/TableSkeleton'
import { useParams } from 'react-router-dom'
import CONFIG_COLUMNS from './config-columns'
import TableView, { useTable, IItem, IColumn } from 'packages/ui/Table'
import { useNavigate } from 'router'
import { useSort } from '../store'
import { shouldHideLine } from '../utils/helpers'
import { GetWireLinesData } from '../use-search'
import EmptyScreen from 'packages/ui/EmptyScreen'
import { useUser } from 'store'
import { trackEvent } from 'analytics'

interface TableProps {
  apiSearch: {
    loading: boolean;
    loadingNext: boolean;
    data?: GetWireLinesData;
    fetchNextPage: () => Promise<void>;
    shouldFetchNextPage: boolean;
  };
}

const Table: React.FC<TableProps> = ({ apiSearch }) => {
  const { id } = useParams()
  const [sort, setSort] = useSort()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [user] = useUser()
  
  const filteredLines = useMemo(() => {
    if (!apiSearch.data || !apiSearch.data.wireLines) return [];
    return apiSearch.data.wireLines.lines.filter(
      line => !shouldHideLine(line.wire_type, line.use_case)
    );
  }, [apiSearch.data]);
  
  const columns = useMemo(() => {
    return CONFIG_COLUMNS.filter(column => {
      if (id && column.hideOnCardOpen) return false;
      if (column.internalOnly && !user?.internal) return false;
      if (column.requiresRole && !user?.admin && !user?.roles?.[column.requiresRole]) return false;
      return true;
    }) as unknown as IColumn[];
  }, [id, user]);
  
  const apiTable = useTable({
    rowKey: 'id',
    loading: apiSearch.loading,
    loadingNext: apiSearch.loadingNext,
    items: filteredLines as unknown as IItem[],
    columns,
    onRowClick: (wireLine: IItem) => {
      trackEvent('wan_details')
      navigate({ pathname: `${wireLine.id}` })
    }
  })

  const isEmpty = !apiSearch.loading && (!filteredLines || filteredLines.length === 0)

  return (
    <Stack
      sx={{
        overflow: 'hidden',
        overflowY: 'auto',
        flex: '1 1 auto',
        scrollbarWidth: 'none',
        height: '100%',
        '&::-webkit-scrollbar ': {
          display: 'none'
        }
      }}
    >
      <ScrollTable
        fetchNextPage={apiSearch.fetchNextPage}
        shouldFetchNextPage={apiSearch.shouldFetchNextPage}
        collapse={Boolean(id)}
        isEmpty={isEmpty}
      >
        {apiSearch.loading ? (
          <TableSkeleton />
        ) : isEmpty ? (
          <EmptyScreen icon="gauge-high">
            {t('wan.no_data_filter')}
          </EmptyScreen>
        ) : (
          <TableView
            apiTable={apiTable}
            sort={{
              ...sort,
              onChange: name => {
                const column = columns.find(column => column.name === name)
                if (column) {
                  setSort(column.sortBy || column.name)
                }
              }
            }}
          />
        )}
      </ScrollTable>
    </Stack>
  )
}

export default Table
